import VectorSource from 'ol/source/Vector';
import { Group as LayerGroup, Tile as TileLayer, Vector as VectorLayer } from 'ol/layer';
import XYZ from 'ol/source/XYZ';
import { ref } from 'vue';
import Map from 'ol/Map';
import View from 'ol/View';
import { fromLonLat, get as getProjection } from 'ol/proj';
import { Fill, Icon, Stroke, Style } from 'ol/style';
import Feature from 'ol/Feature';
import { Point } from 'ol/geom';
import GeoJSON from 'ol/format/GeoJSON';
import circle from '@turf/circle';
import { getTopLeft, getWidth } from 'ol/extent';
import WMTS from 'ol/source/WMTS';
import WMTSTileGrid from 'ol/tilegrid/WMTS';
import TileGrid from 'ol/tilegrid/TileGrid';
import TileWMS from 'ol/source/TileWMS';
import { useDefaultsListStore } from '@/stores/defaultsListStore';
import { storeToRefs } from 'pinia';
import { createStyle } from '@/helpers/mapStyleHelper';
import { ForestreeIconFactory } from '@/config/ForestreeIconFactory.js';
import defaultTileLayers from '@/config/defaultTileLayers.js';

const useTreeReportMap = () => {
    const defaultsListStore = useDefaultsListStore();
    const { defaults, homeCenter } = storeToRefs(defaultsListStore);
    const map = ref({});
    const mapCreated = ref(false);
    const treeLayer = ref(null);
    const treeSource = ref(new VectorSource());
    const tpzLayer = ref(null);
    const tpzSource = ref(new VectorSource());
    const srzLayer = ref(null);
    const srzSource = ref(new VectorSource());
    const aerialGroup = ref(null);
    const aerial = ref(new TileLayer());
    const imageService = ref(new TileLayer({ visible: false }));
    const imageService2 = ref(new TileLayer({ visible: false }));
    const satellite = ref(defaultTileLayers().satelliteTileLayer);
    const osm = ref(defaultTileLayers().osmTileLayer);
    const labels = ref(defaultTileLayers().labelsTileLayer);

    const createMap = (tree) => {
        var homeLng = parseFloat(tree.lng);
        var homeLat = parseFloat(tree.lat);

        map.value = new Map({
            target: 'minimap',
            layers: [osm.value, satellite.value],
            view: new View({
                projection: 'EPSG:3857',
                center: fromLonLat([homeLng, homeLat], 'EPSG:3857'),
                zoom: 18
            })
        });

        mapCreated.value = true;

        map.value.once('postrender', (event) => {
            addImagery();
            addLayers(tree);
        });
    };
    const addLayers = (tree) => {
        var getRiskStyle = (feature, resolution) => {
            const style = createStyle({
                image: ForestreeIconFactory().whiteLeafIcon
            });
            return style;
        };

        var getStyleTPZ = (feature, resolution) => {
            var accuracy = feature.get('accuracy');
            var dash = accuracy == 'low' ? [3, 6] : [1, 1];
            return new Style({
                fill: new Fill({
                    color: 'rgba(251, 146, 60, 0.3)'
                }),
                stroke: new Stroke({
                    width: 1.5,
                    color: 'rgba(251, 146, 60, 0.9)',
                    lineDash: dash
                })
            });
        };

        var getStyleTPZ = (feature, resolution) => {
            var accuracy = feature.get('accuracy');
            var dash = accuracy == 'low' ? [3, 6] : [1, 1];
            return new Style({
                fill: new Fill({
                    color: 'rgba(251, 146, 60, 0.3)'
                }),
                stroke: new Stroke({
                    width: 1.5,
                    color: 'rgba(251, 146, 60, 0.9)',
                    lineDash: dash
                })
            });
        };

        var getStyleSRZ = (feature, resolution) => {
            var accuracy = feature.get('accuracy');
            var dash = accuracy == 'low' ? [3, 6] : [1, 1];
            return new Style({
                fill: new Fill({
                    color: 'rgba(220, 38, 38, 0.2)'
                }),
                stroke: new Stroke({
                    width: 1.5,
                    color: 'rgba(250, 250, 250, 0.9)',
                    lineDash: dash
                })
            });
        };

        tpzLayer.value = new VectorLayer({
            source: tpzSource.value,
            style: getStyleTPZ
        });
        map.value.addLayer(tpzLayer.value);

        srzLayer.value = new VectorLayer({
            source: srzSource.value,
            style: getStyleSRZ
        });
        map.value.addLayer(srzLayer.value);

        treeLayer.value = new VectorLayer({
            source: treeSource.value,
            style: getRiskStyle
        });
        map.value.addLayer(treeLayer.value);

        var treeFeature = new Feature(new Point(fromLonLat([parseFloat(tree.lng), parseFloat(tree.lat)], 'EPSG:3857')));
        treeFeature.setId(tree.id);
        treeFeature.set('riskId', tree.trees_risk_id);
        treeFeature.set('treeStatus', tree.trees_status_id);
        treeSource.value.addFeature(treeFeature);

        var dbhAccuracy = parseInt(tree.dbh) > 0 ? 'high' : 'low';

        var turfFormat = new GeoJSON();

        if (tree.tpz) {
            var makeTurfCircleTPZ = circle(
                [parseFloat(tree.lng), parseFloat(tree.lat)],
                tree.tpz ? parseFloat(tree.tpz) / 1000 : 0,
                { steps: 64, units: 'kilometers' }
            );
            var tpzGeom = turfFormat.readFeature(makeTurfCircleTPZ);

            var tpzFeature = new Feature({
                geometry: tpzGeom.getGeometry().transform('EPSG:4326', 'EPSG:3857')
            });

            tpzFeature.setId(tree.id);
            tpzFeature.set('tpz', tree.tpz);
            tpzFeature.set('accuracy', dbhAccuracy);
            tpzSource.value.addFeature(tpzFeature);
        }

        if (tree.srz) {
            var makeTurfCircleSRZ = circle(
                [parseFloat(tree.lng), parseFloat(tree.lat)],
                tree.srz ? parseFloat(tree.srz) / 1000 : 0,
                { steps: 64, units: 'kilometers' }
            );
            var srzGeom = turfFormat.readFeature(makeTurfCircleSRZ);

            var srzFeautre = new Feature({
                geometry: srzGeom.getGeometry().transform('EPSG:4326', 'EPSG:3857')
            });
            srzFeautre.setId(tree.id);
            srzFeautre.set('srz', tree.srz);
            srzFeautre.set('accuracy', dbhAccuracy);
            srzSource.value.addFeature(srzFeautre);
        }
    };
    const addImagery = () => {
        if (defaults.value && defaults.value.imagery) {
            var imageUrl = '';
            var source = null;
            var source2 = null;
            var visState = false;

            if (defaults.value.imagery.provider === 'metromap') {
                imageUrl = `https://pathapi.metromap.com.au/api-link/${defaults.value.imagery.key}/wmts/Australian_capital_cities_latest/default/GoogleMapsCompatibleExt:epsg:3857/{z}/{y}/{x}.jpgpng`;
                source = new XYZ({
                    urls: [imageUrl],
                    tilePixelRatio: 1
                });
            }

            if (defaults.value.imagery.provider === 'metromap-xyz') {
                imageUrl = `https://api-v3.metromap.com.au/tiles/xyz/Australian_capital_cities_latest/{z}/{y}/{x}.jpgpng?key=${defaults.value.imagery.key}`;
                source = new XYZ({
                    urls: [imageUrl],
                    tilePixelRatio: 1
                });
            }

            if (defaults.value.imagery.provider === 'metromap-wmts') {
                const projection = getProjection('EPSG:3857');
                const projectionExtent = projection.getExtent();
                const size = getWidth(projectionExtent) / 256;
                const resolutions = new Array(22);
                const matrixIds = new Array(22);
                for (let z = 0; z < 22; ++z) {
                    resolutions[z] = size / Math.pow(2, z);
                    matrixIds[z] = z;
                }

                source = new WMTS({
                    url: `https://api.metromap.com.au/ogc/key/${defaults.value.imagery.key}/service?SERVICE=WMTS`,
                    layer: 'Australian_capital_cities_latest',
                    matrixSet: 'webmercator',
                    format: 'image/png',
                    projection: projection,
                    tileGrid: new WMTSTileGrid({
                        origin: getTopLeft(projectionExtent),
                        resolutions: resolutions,
                        matrixIds: matrixIds
                    }),
                    style: 'default',
                    wrapX: true
                });
            }

            if (defaults.value.imagery.provider === 'nearmap') {
                imageUrl = `https://au0.nearmap.com/maps/z={z}&x={x}&y={y}&version=2&nml=Vert&client=wmts_integration&httpauth=false&apikey=${defaults.value.imagery.key}`;
                source = new XYZ({
                    urls: [imageUrl],
                    tilePixelRatio: 1
                });
            }

            if (defaults.value.imagery.provider === 'esri' && defaults.value.imagery.url) {
                imageUrl = `${defaults.value.imagery.url}/tile/{z}/{y}/{x}`;
                source = new XYZ({
                    urls: [imageUrl],
                    tileSize: 512,
                    maxZoom: 19
                });
            }

            if (defaults.value.imagery.provider === 'victoria') {
                const projExtent = getProjection('EPSG:3857').getExtent();
                const startResolution = getWidth(projExtent) / 512;
                const resolutions = new Array(22);
                for (let i = 0, ii = resolutions.length; i < ii; ++i) {
                    resolutions[i] = startResolution / Math.pow(2, i);
                }
                const tileGrid = new TileGrid({
                    extent: projExtent,
                    resolutions: resolutions,
                    tileSize: [512, 512]
                });

                source = new TileWMS({
                    url: 'https://base.maps.vic.gov.au/service?',
                    params: { LAYERS: 'AERIAL_WM', TILED: true },
                    transition: 0,
                    tileGrid: tileGrid
                });
            }

            if (defaults.value.imagery.provider === 'dual-wms') {
                const projExtent = getProjection('EPSG:3857').getExtent();
                const startResolution = getWidth(projExtent) / 512;
                const resolutions = new Array(22);
                for (let i = 0, ii = resolutions.length; i < ii; ++i) {
                    resolutions[i] = startResolution / Math.pow(2, i);
                }
                const tileGrid = new TileGrid({
                    extent: projExtent,
                    resolutions: resolutions,
                    tileSize: [512, 512]
                });

                source = new TileWMS({
                    url: defaults.value.imagery.url,
                    transition: 0,
                    tileGrid: tileGrid,
                    format: 'image/png'
                });

                source2 = new TileWMS({
                    url: defaults.value.imagery.url2,
                    transition: 0,
                    tileGrid: tileGrid
                });
            }

            imageService.value = new TileLayer({
                source: source,
                visible: visState
            });

            imageService2.value = new TileLayer({
                source: source2,
                visible: visState,
                crossOrigin: 'anonymous'
            });

            aerialGroup.value = new LayerGroup({
                layers: [imageService2.value, imageService.value, labels.value]
            });

            labels.value.setVisible(visState);

            var layers = map.value.getLayers();
            layers.insertAt(0, aerialGroup.value);
        } else if (defaults.value && homeCenter.value.tile) {
            if (homeCenter.value.tile === 'botanic' || homeCenter.value.tile === 'waite') {
                aerial.value = new TileLayer({
                    source: new XYZ({
                        urls: [`/tile/tileserver.php?/index.json?/${homeCenter.value.tile}/{z}/{x}/{y}.jpg`],
                        minZoom: 12,
                        maxZoom: 20,
                        tilePixelRatio: 1
                    }),
                    visible: false
                });

                aerialGroup.value = new LayerGroup({
                    layers: [aerial.value, labels.value]
                });

                var layers = map.value.getLayers();
                layers.insertAt(0, aerialGroup.value);
            } else if (homeCenter.value.tile === 'chb' || homeCenter.value.tile === 'cop') {
                aerial.value = new TileLayer({
                    source: new XYZ({
                        urls: [
                            `https://tile.forestree.dev/tile/${homeCenter.value.tile}/{z}/{x}/{y}.jpg`
                        ],
                        minZoom: 12,
                        maxZoom: 20,
                        tilePixelRatio: 1
                    }),
                    visible: false
                });

                aerialGroup.value = new LayerGroup({
                    layers: [aerial.value, labels.value]
                });

                var layers = map.value.getLayers();
                layers.insertAt(0, aerialGroup.value);
            } else {
                var projection = getProjection('EPSG:3857');
                var projectionExtent = projection.getExtent();
                var size = getWidth(projectionExtent) / 256;
                var resolutions = new Array(14);
                var matrixIds = new Array(14);
                for (var z = 0; z < 22; ++z) {
                    resolutions[z] = size / Math.pow(2, z);
                    matrixIds[z] = 'EPSG:900913:' + z;
                }

                var imgSource = new WMTS({
                    attributions: '',
                    url: 'https://geoserver.forestree.technology/geoserver/gwc/service/wmts',
                    layer: 'forestree:' + homeCenter.value.tile,
                    matrixSet: 'EPSG:900913',
                    format: 'image/vnd.jpeg-png',
                    projection: projection,
                    tileGrid: new WMTSTileGrid({
                        origin: getTopLeft(projectionExtent),
                        resolutions: resolutions,
                        matrixIds: matrixIds
                    }),
                    style: '',
                    wrapX: true
                });

                aerial.value = new TileLayer({
                    source: imgSource,
                    visible: false,
                    crossOrigin: 'anonymous'
                });

                aerialGroup.value = new LayerGroup({
                    layers: [aerial.value, labels.value]
                });

                var layers = map.value.getLayers();
                layers.insertAt(0, aerialGroup.value);
            }
        }
    };
    const baseMapSwitch = () => {
        if (osm.value.getVisible()) {
            if (defaults.value && defaults.value.imagery) {
                imageService.value.setVisible(true);
                imageService2.value.setVisible(true);
                labels.value.setVisible(true);
            } else if (defaults.value && homeCenter.value.tile) {
                aerial.value.setVisible(true);
                labels.value.setVisible(true);
            } else {
                satellite.value.setVisible(true);
                labels.value.setVisible(true);
            }
            osm.value.setVisible(false);
        } else {
            if (defaults.value && defaults.value.imagery) {
                imageService.value.setVisible(false);
                imageService2.value.setVisible(false);
                labels.value.setVisible(false);
            } else if (defaults.value && homeCenter.value.tile) {
                aerial.value.setVisible(false);
                labels.value.setVisible(false);
            } else {
                satellite.value.setVisible(false);
                labels.value.setVisible(false);
            }
            osm.value.setVisible(true);
        }
    };

    return {
        map,
        mapCreated,
        osm,
        satellite,
        labels,
        imageService,
        aerial,
        aerialGroup,
        createMap,
        addImagery,
        addLayers,
        baseMapSwitch
    };
};

export default useTreeReportMap;
